import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  [
    'inline-flex items-center justify-center',
    'font-semibold',
    'transition-colors duration-150',
  ],
  {
    variants: {
      variant: {
        default: 'bg-[var(--primary)] text-white',
        secondary: 'bg-[var(--surface)] text-[var(--text-secondary)] border border-[var(--border-default)]',
        outline: 'bg-transparent border border-[var(--primary)] text-[var(--primary)]',
        success: 'bg-[var(--success)] text-white',
        warning: 'bg-[var(--warning)] text-white',
        error: 'bg-[var(--error)] text-white',
        info: 'bg-[var(--info)] text-white',
        
        // Мягкие версии
        'success-subtle': 'bg-[var(--success)]/10 text-[var(--success)]',
        'warning-subtle': 'bg-[var(--warning)]/10 text-[var(--warning)]',
        'error-subtle': 'bg-[var(--error-bg)] text-[var(--error)]',
        'info-subtle': 'bg-[var(--info-bg)] text-[var(--info)]',
        'primary-subtle': 'bg-[var(--primary-subtle)] text-[var(--primary)]',
        
        // Специальные
        new: 'bg-[var(--lime-500)] text-black font-bold',
        popular: 'bg-[var(--primary)] text-white font-bold',
        sale: 'bg-[var(--rose-500)] text-white font-bold',
        spicy: 'bg-[var(--error)] text-white',
        scorched: 'bg-[#FBBF24] text-black font-bold',
        mini: 'bg-[#22D3EE] text-white font-bold',
        
        // Dot indicator
        dot: 'bg-[var(--primary)]',
      },
      // Бейдж — ступень pill по контракту, на всех размерах.
      // Кегль 10 переведён на роль t-nano: ровно та ступень, которую под него
      // и достроили (ответ 38 = А), размер прежний.
      size: {
        xs: 't-nano px-1.5 py-0.5 rounded-pill',
        sm: 'text-xs px-2 py-0.5 rounded-pill',
        default: 'text-xs px-2.5 py-1 rounded-pill',
        lg: 'text-sm px-3 py-1 rounded-pill',
        dot: 'h-2 w-2 rounded-pill p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, dot, children, ...props }, ref) => {
    if (dot) {
      return (
        <span
          ref={ref}
          className={cn(badgeVariants({ variant: 'dot', size: 'dot' }), className)}
          {...props}
        />
      );
    }

    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };

